/**
 * SA-469 (spec D5): the agenda ↔ process relation, from the library list a
 * page already loads — a process row's `record_agenda`, read both ways. An
 * agenda that more than one process binds names no process, mirroring
 * `find_process_for`, which starts nothing then; `startingProcess` (SA-476)
 * adds the flag.
 */
import type { WorkflowSummary } from '@/hooks/useWorkflows'
import { agendaLabel } from '@/utils/workflowOverview'

export type IntakeRow = Pick<WorkflowSummary, 'id' | 'name' | 'kind'> & { record_agenda?: string | null }

const bindersOf = (agendaId: string, rows: readonly IntakeRow[]) =>
  rows.filter((r) => r.kind === 'process' && r.record_agenda === agendaId)

/** The one process that binds `agendaId`, else null (none, or ambiguous). */
export function processForAgenda(agendaId: string, rows: readonly IntakeRow[]): IntakeRow | null {
  const matches = bindersOf(agendaId, rows)
  return matches.length === 1 ? matches[0] : null
}

/**
 * SA-476 (spec D2): whether a record born under `agendaId` will have a
 * process start — `find_process_for`'s rule (exactly one binder) under the
 * `process_engine` flag. `engine` is the STRICT read (null until the manifest
 * loads) and `rows` is undefined until the library loads: either unknown is
 * `unknown`, and a caller promises nothing then.
 */
export type StartState =
  | { kind: 'starts'; process: IntakeRow }
  | { kind: 'engine_off' }
  | { kind: 'ambiguous' }
  | { kind: 'none' }
  | { kind: 'unknown' }

export function startingProcess(agendaId: string, rows: readonly IntakeRow[] | undefined, engine: boolean | null): StartState {
  if (engine === null || rows === undefined) return { kind: 'unknown' }
  const binders = bindersOf(agendaId, rows)
  if (binders.length === 0) return { kind: 'none' }
  if (!engine) return { kind: 'engine_off' }
  return binders.length === 1 ? { kind: 'starts', process: binders[0] } : { kind: 'ambiguous' }
}

export type IntakeRelation =
  | { kind: 'process'; agendaLabel: string }
  | { kind: 'agenda'; processName: string }

/** What a library card says: a process names its agenda; a bound agenda names its process. */
export function intakeRelation(row: IntakeRow, rows: readonly IntakeRow[]): IntakeRelation | null {
  if (row.kind === 'process') return row.record_agenda ? { kind: 'process', agendaLabel: agendaLabel(row.record_agenda) } : null
  if (row.kind !== 'order') return null
  const process = processForAgenda(row.id, rows)
  return process ? { kind: 'agenda', processName: process.name } : null
}
